/**
 * useStandardFooterSlot — registers the Standard tab's "Apply to <tracker>"
 * action, discard and unsaved state in the drawer footer/header.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Split out of StandardConfigTabV2 (over the file limit). The tracker comes
 * from the draft context, so a Tres Commas drawer applies to Tres Commas and
 * says so; Voluum's copy is unchanged.
 */

import { useCallback, useEffect, useState } from 'react';
import { trackerLabel } from '@trafficpulse/shared';

import {
  useFlowOptimizerDrawerFooter,
  type FlowOptimizerSaveAction,
} from '@/features/flow-optimizer/FlowOptimizerDrawerFooterContext';
import { useStandardDraftContext } from '@/features/flow-optimizer/standard-draft';
import type { FlowApplyFailureNotice } from '@/features/flow-optimizer/tabs/standard/v2/flow-apply';

import { useStandardActions } from './useStandardActions';

export interface UseStandardFooterSlotParams {
  flowId: string;
  killSwitch: boolean;
  standardTabActive: boolean;
}

function confirmApply(trackerName: string, bullets: string[]): boolean {
  const summary = bullets.join('\n• ');
  const message = summary
    ? `Apply these changes to ${trackerName}?\n\n• ${summary}\n\nA snapshot will be created automatically.`
    : `Apply changes to ${trackerName}?`;
  return window.confirm(message);
}

function useApplyHandlers(flowId: string) {
  const { draftState, provider } = useStandardDraftContext();
  const actions = useStandardActions({ flowId, provider });
  const trackerName = trackerLabel(provider);
  const [applyNotice, setApplyNotice] = useState<FlowApplyFailureNotice | null>(null);

  const handleDiscard = useCallback(() => {
    setApplyNotice(null);
    draftState.reset();
  }, [draftState.reset]);

  const handleApply = useCallback(async () => {
    if (!draftState.draft) return;
    if (!confirmApply(trackerName, draftState.diff.bullets)) return;
    setApplyNotice(null);
    const result = await actions.apply(draftState.draft);
    if (!result.ok) setApplyNotice(result.notice);
  }, [draftState.draft, draftState.diff.bullets, actions, trackerName]);

  return { actions, trackerName, applyNotice, setApplyNotice, handleDiscard, handleApply };
}

type ApplyHandlers = ReturnType<typeof useApplyHandlers>;

function applyAction(h: ApplyHandlers, disabled: boolean): FlowOptimizerSaveAction {
  return {
    id: 'apply-voluum',
    label: `Apply to ${h.trackerName}`,
    variant: 'primary',
    onClick: h.handleApply,
    disabled,
    helpText: `Sends the Standard tab flow to ${h.trackerName} through TrafficPulse. The server merges your edits, validates the payload, and creates a ${h.trackerName} snapshot.`,
  };
}

type FooterSetters = Pick<
  ReturnType<typeof useFlowOptimizerDrawerFooter>,
  'setSlot' | 'setHeaderSlot'
>;

interface ReadySlots {
  h: ApplyHandlers;
  flowName: string;
  dirty: boolean;
  applying: boolean;
  applyDisabled: boolean;
  flowQuery: ReturnType<typeof useStandardDraftContext>['flowQuery'];
}

function clearSlots({ setSlot, setHeaderSlot }: FooterSetters): void {
  setSlot(null);
  setHeaderSlot(null);
}

/** Not ready (other tab, loading, failed): no footer/header, no stale notice. */
function resetSlots(h: ApplyHandlers, setters: FooterSetters): void {
  h.setApplyNotice(null);
  clearSlots(setters);
}

function publishSlots(setters: FooterSetters, s: ReadySlots): void {
  const { h, dirty, applying, flowQuery } = s;
  const actions = [applyAction(h, s.applyDisabled)];
  const { applyNotice, handleDiscard: onDiscard } = h;
  setters.setSlot({ dirty, applying, applyNotice, onDiscard, actions });
  setters.setHeaderSlot({
    flowName: s.flowName,
    dirty,
    syncing: flowQuery.isFetching,
    onRefresh: () => void flowQuery.refetch(),
  });
}

export function useStandardFooterSlot(params: UseStandardFooterSlotParams): void {
  const { setSlot, setHeaderSlot } = useFlowOptimizerDrawerFooter();
  const { flowQuery, draftState } = useStandardDraftContext();
  const h = useApplyHandlers(params.flowId);
  const applyDisabled =
    !draftState.dirty || draftState.issues.length > 0 || h.actions.applying || params.killSwitch;
  const ready =
    params.standardTabActive && !flowQuery.isLoading && !flowQuery.isError && draftState.draft;
  const { dirty } = draftState;
  const { applying } = h.actions;

  useEffect(() => {
    const setters = { setSlot, setHeaderSlot };
    if (!ready) {
      resetSlots(h, setters);
      return;
    }
    const flowName = ready.name ?? '';
    publishSlots(setters, { h, flowName, dirty, applying, applyDisabled, flowQuery });
    return () => clearSlots(setters);
  }, [ready, dirty, applying, applyDisabled, h.handleApply, h.handleDiscard, h.applyNotice,
    h.trackerName, setSlot, setHeaderSlot, flowQuery.isFetching]);
}
